import axios from 'axios';

/**
 * Which API host this portal talks to.
 *
 * Each tenant has its own: acme.portal.example.com talks to
 * acme.api.example.com. VITE_API_URL is baked into the build and is the
 * same for every tenant, so the tenant part has to come from the hostname
 * the browser is actually on. The rules mirror slugFromHost on the backend
 * (src/config/tenancy.js) and must stay in step with it: at least three
 * labels, the first one a legal slug, and none of the reserved labels.
 *
 * The apex portal (portal.example.com) and localhost keep the configured
 * value. A custom domain cannot be derived this way at all; Phase 5 asks
 * GET /api/tenant/resolve for it.
 */
const RESERVED_LABELS = ['www', 'api', 'app', 'admin', 'portal', 'static', 'cdn', 'mail', 'manage'];
const SLUG_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

function tenantSlugFromHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;
  const labels = host.split('.');
  if (labels.length < 3) return null;
  const [first] = labels;
  if (RESERVED_LABELS.includes(first) || !SLUG_RE.test(first)) return null;
  return first;
}

function apiBaseUrl(configured = import.meta.env.VITE_API_URL || '/api', hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  try {
    if (!/^https?:\/\//i.test(configured)) return configured; // relative: same origin, nothing to derive
    const slug = tenantSlugFromHost(hostname);
    if (!slug) return configured;
    const api = new URL(configured);
    if (api.hostname.split('.')[0] === slug) return configured; // already tenant-scoped
    api.hostname = `${slug}.${api.hostname}`;
    return api.toString().replace(/\/+$/, '');
  } catch {
    return configured;
  }
}

export const API_BASE_URL = apiBaseUrl();

/**
 * API client instance
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * Set authorization token
 */
api.setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

/**
 * Token refresh lock to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (success) => {
  refreshSubscribers.forEach((callback) => callback(success));
  refreshSubscribers = [];
};

/**
 * Request interceptor
 */
api.interceptors.request.use(
  (config) => {
    // You can add request logging here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 */
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    // Don't intercept 401 on auth endpoints to prevent refresh loops. Logout
    // above all: an expired session's logout call is itself a 401, and
    // answering it with another logout looped without end (2026-10-30: a
    // stale tab sent thousands of requests and locked its owner out).
    const isAuthEndpoint = originalRequest.url?.includes('/auth/refresh') ||
                           originalRequest.url?.includes('/auth/login') ||
                           originalRequest.url?.includes('/auth/logout') ||
                           originalRequest.url?.includes('/auth/register');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      const { useAuthStore } = await import('../context/authStore');
      const store = useAuthStore.getState();

      if (!store.refreshToken) {
        // No refresh token, logout and reject
        store.logout();
        return Promise.reject(error);
      }

      // If already refreshing, wait for it to complete
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((success) => {
            if (success) {
              resolve(api(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      // Start refresh
      isRefreshing = true;

      try {
        await store.refreshSession();
        isRefreshing = false;
        onTokenRefreshed(true);
        // Retry original request
        return api(originalRequest);
      } catch {
        isRefreshing = false;
        onTokenRefreshed(false);
        // Refresh failed, logout
        store.logout();
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Export specific API methods for convenience
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token, password) =>
    api.post('/auth/verify-email', { token, password }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  getProviders: () => api.get('/auth/providers'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const ticketsAPI = {
  list: ({ companyId, ...params } = {}) => api.get('/tickets', { params: { companyId, ...params } }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  getNotes: (id) => api.get(`/tickets/${id}/notes`),
  // audience 'team': a note for the company's own IT team and the support
  // provider, never the requester (co-managed IT).
  addNote: (id, text, { audience = 'requester' } = {}) => api.post(`/tickets/${id}/notes`, { text, audience }),
  close: (id) => api.post(`/tickets/${id}/close`),
  // Co-managed IT: a client technician working the company's queue.
  work: (id) => api.get(`/tickets/${id}/work`),
  setAssignee: (id, portalUserId) => api.put(`/tickets/${id}/assignee`, { portalUserId }),
  setStatus: (id, statusId) => api.patch(`/tickets/${id}/status`, { statusId }),
  escalate: (id, reason) => api.post(`/tickets/${id}/escalate`, { reason }),
  logTime: (id, data) => api.post(`/tickets/${id}/time`, data),
  getDocuments: (id) => api.get(`/tickets/${id}/documents`),
  downloadDocument: (ticketId, documentId) => api.get(`/tickets/${ticketId}/documents/${documentId}/download`, {
    responseType: 'blob',
  }),
  uploadDocuments: (id, files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post(`/tickets/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const invoicesAPI = {
  list: ({ companyId, ...params } = {}) => api.get('/invoices', { params: { companyId, ...params } }),
  getOpen: ({ companyId } = {}) => api.get('/invoices/open', { params: { companyId } }),
  get: (id, config = {}) => api.get(`/invoices/${id}`, config),
  getPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  // View tracking
  getViews: (id) => api.get(`/invoices/${id}/views`),
  trackPdfDownload: (id, viewId) => api.post(`/invoices/${id}/track-pdf-download`, {}, {
    headers: viewId ? { 'X-View-Id': viewId } : {}
  }),
};

export const paymentsAPI = {
  getSettings: () => api.get('/payments/settings'),
  // Which gateway takes payments, and what the card form needs: { gateway, publishableKey, stripeAccount, capabilities }
  getGateway: () => api.get('/payments/gateway'),
  // Start saving a method: { clientSecret } on the hosted card form, null when the portal form posts the card
  beginSavedMethod: (methodType = 'card', { companyId } = {}) => api.post('/payments/methods/begin', { methodType }, { params: { companyId } }),
  getMethods: ({ companyId } = {}) => api.get('/payments/methods', { params: { companyId } }),
  addCard: (data, { companyId } = {}) => api.post('/payments/methods/card', data, { params: { companyId } }),
  addBank: (data, { companyId } = {}) => api.post('/payments/methods/bank', data, { params: { companyId } }),
  deleteMethod: (id, { companyId } = {}) => api.delete(`/payments/methods/${id}`, { params: { companyId } }),
  setDefaultMethod: (id, { companyId } = {}) => api.patch(`/payments/methods/${id}/default`, {}, { params: { companyId } }),
  charge: (data, { companyId } = {}) => api.post('/payments/charge', data, { params: { companyId } }),
  getHistory: (params) => api.get('/payments/history', { params }),
  getAutopay: ({ companyId } = {}) => api.get('/payments/autopay', { params: { companyId } }),
  setAutopay: (data, { companyId } = {}) => api.post('/payments/autopay', data, { params: { companyId } }),
  disableAutopay: ({ companyId } = {}) => api.delete('/payments/autopay', { params: { companyId } }),
  getAutopayStatus: ({ companyId } = {}) => api.get('/payments/autopay/status', { params: { companyId } }),
};

export const recurringAPI = {
  list: ({ companyId } = {}) => api.get('/recurring', { params: { companyId } }),
  create: (data, { companyId } = {}) => api.post('/recurring', data, { params: { companyId } }),
  update: (id, data, { companyId } = {}) => api.patch(`/recurring/${id}`, data, { params: { companyId } }),
  cancel: (id, { companyId } = {}) => api.delete(`/recurring/${id}`, { params: { companyId } }),
  syncStatus: (id, { companyId } = {}) => api.post(`/recurring/${id}/sync`, {}, { params: { companyId } }),
};

export const quotesAPI = {
  list: ({ companyId } = {}) => api.get('/quotes', { params: { companyId } }),
  get: (id) => api.get(`/quotes/${id}`),
  // Public quote viewing (no auth)
  view: (token) => api.get(`/quotes/view/${token}`),
  addComment: (token, data) => api.post(`/quotes/view/${token}/comments`, data),
  // Authenticated quote comments
  getComments: (opportunityId) => api.get(`/quotes/${opportunityId}/comments`),
  addPortalComment: (opportunityId, message) => api.post(`/quotes/${opportunityId}/comments`, { message }),
  accept: (token, data) => api.post(`/quotes/${token}/accept`, data),
  // Local QuoteWerks quotes
  getLocal: (id, config = {}) => api.get(`/quotes/local/${id}`, config),
  updateSelections: (id, data) => api.patch(`/quotes/local/${id}/selections`, data),
  approve: (id, data) => api.post(`/quotes/local/${id}/approve`, data),
  reject: (id, reason) => api.post(`/quotes/local/${id}/reject`, { reason }),
  downloadPdf: (id) => api.get(`/quotes/local/${id}/pdf`, { responseType: 'blob' }),
  // View engagement tracking
  trackPdfDownload: (quoteId, viewId) => api.post(`/quotes/${quoteId}/track-pdf-download`, {}, {
    headers: viewId ? { 'X-View-Id': viewId } : {}
  }),
};

export const agreementsAPI = {
  list: ({ companyId } = {}) => api.get('/agreements', { params: { companyId } }),
  get: (id) => api.get(`/agreements/${id}`),
};

export const configurationsAPI = {
  list: ({ companyId } = {}) => api.get('/configurations', { params: { companyId } }),
  get: (id) => api.get(`/configurations/${id}`),
};

export const projectsAPI = {
  list: ({ companyId } = {}) => api.get('/projects', { params: { companyId } }),
  get: (id) => api.get(`/projects/${id}`),
  getTickets: (id) => api.get(`/projects/${id}/tickets`),
  getIssues: (id) => api.get(`/projects/${id}/issues`),
};

export const usersAPI = {
  updateProfile: (data) => api.patch('/users/profile', data),
  getNotifications: () => api.get('/users/notifications'),
  updateNotifications: (data) => api.patch('/users/notifications', data),
};

export const emailTemplatesAPI = {
  list: (params) => api.get('/admin/email-templates', { params }),
  get: (id) => api.get(`/admin/email-templates/${id}`),
  create: (data) => api.post('/admin/email-templates', data),
  update: (id, data) => api.put(`/admin/email-templates/${id}`, data),
  delete: (id) => api.delete(`/admin/email-templates/${id}`),
  validate: (mjmlSource) => api.post('/admin/email-templates/validate', { mjml_source: mjmlSource }),
  preview: (id, variables) => api.post(`/admin/email-templates/${id}/preview`, { variables }),
  sendTest: (id, email, variables) => api.post(`/admin/email-templates/${id}/test`, { email, variables }),
  getVersions: (id) => api.get(`/admin/email-templates/${id}/versions`),
  restoreVersion: (id, versionId) => api.post(`/admin/email-templates/${id}/versions/${versionId}/restore`),
};

export const apiKeysAPI = {
  list: () => api.get('/admin/api-keys'),
  permissions: () => api.get('/admin/api-keys/permissions'),
  get: (id) => api.get(`/admin/api-keys/${id}`),
  create: (data) => api.post('/admin/api-keys', data),
  update: (id, data) => api.patch(`/admin/api-keys/${id}`, data),
  delete: (id) => api.delete(`/admin/api-keys/${id}`),
  rotate: (id) => api.post(`/admin/api-keys/${id}/rotate`),
  getAudit: (id, params) => api.get(`/admin/api-keys/${id}/audit`, { params }),
};

export const adminQuotesAPI = {
  list: (params) => api.get('/admin/quotes', { params }),
  get: (id) => api.get(`/admin/quotes/${id}`),
  create: (data) => api.post('/admin/quotes', data),
  update: (id, data) => api.patch(`/admin/quotes/${id}`, data),
  publish: (id) => api.post(`/admin/quotes/${id}/publish`),
  searchCompanies: (q) => api.get('/admin/quotes/companies/search', { params: { q } }),
  getCompanyContacts: (companyId) => api.get(`/admin/quotes/companies/${companyId}/contacts`),
  searchProducts: (q) => api.get('/admin/quotes/products/search', { params: { q } }),
  getStages: () => api.get('/admin/quotes/stages'),
  getCompanyTaxRate: (companyId) => api.get(`/admin/quotes/companies/${companyId}/tax-rate`),
  downloadPdf: (id) => api.get(`/admin/quotes/${id}/pdf`, { responseType: 'blob' }),
  updateStatus: (id, status, type = 'quote') => api.patch(`/admin/quotes/${id}/status`, { status, type }),
  getOpportunityStatuses: () => api.get('/admin/quotes/opportunity-statuses'),
  // Quote comments
  getComments: (opportunityId) => api.get(`/admin/quotes/${opportunityId}/comments`),
  addComment: (opportunityId, data) => api.post(`/admin/quotes/${opportunityId}/comments`, data),
  markAllCommentsRead: (opportunityId) => api.post(`/admin/quotes/${opportunityId}/comments/mark-all-read`),
  getQuestionsSummary: (limit = 10) => api.get('/admin/quotes/questions/summary', { params: { limit } }),
  // Ticket settings
  getTicketSettings: () => api.get('/admin/quotes/ticket-settings'),
  updateTicketSettings: (data) => api.put('/admin/quotes/ticket-settings', data),
  // Quote layout: sections, part numbers, shipping, account manager, about text, purchase order
  getPresentation: () => api.get('/admin/quotes/presentation'),
  updatePresentation: (data) => api.put('/admin/quotes/presentation', data),
  getServiceBoards: () => api.get('/admin/service-boards'),
  getBoardStatuses: (boardId) => api.get(`/admin/service-boards/${boardId}/statuses`),
  // Quote question ticket
  getQuoteTicket: (opportunityId) => api.get(`/admin/quotes/${opportunityId}/ticket`),
  addTicketNote: (opportunityId, message) => api.post(`/admin/quotes/${opportunityId}/ticket/notes`, { message }),
};

export const adminInvoicesAPI = {
  list: (params) => api.get('/admin/invoices', { params }),
  get: (id) => api.get(`/admin/invoices/${id}`),
  applyPayment: (id, data) => api.post(`/admin/invoices/${id}/payments`, data),
  updatePayment: (id, paymentId, data) => api.put(`/admin/invoices/${id}/payments/${paymentId}`, data),
  deletePayment: (id, paymentId) => api.delete(`/admin/invoices/${id}/payments/${paymentId}`),
  updateStatus: (id, statusId) => api.patch(`/admin/invoices/${id}/status`, { statusId }),
  getBillingStatuses: () => api.get('/admin/billing-statuses'),
  getPaymentTypes: () => api.get('/admin/payment-types'),
  // Payment breakdown - syncs to CW automatically
  updatePaymentBreakdown: (breakdownId, data) => api.patch(`/admin/payment-breakdown/${breakdownId}`, data),
  deletePaymentBreakdown: (breakdownId) => api.delete(`/admin/payment-breakdown/${breakdownId}`),
  // Company-specific invoice endpoints
  getCompanyInvoices: (companyId, params) => api.get(`/admin/companies/${companyId}/invoices`, { params }),
  getCompanyPayments: (companyId, params) => api.get(`/admin/companies/${companyId}/payments`, { params }),
  getCompanyInvoiceStats: (companyId) => api.get(`/admin/companies/${companyId}/invoice-stats`),
  // Credit memo endpoints
  getCompanyCreditMemos: (companyId) => api.get(`/admin/companies/${companyId}/credit-memos`),
};

export const adminAgreementsAPI = {
  list: (params) => api.get('/admin/agreements', { params }),
  get: (id) => api.get(`/admin/agreements/${id}`),
};

// Admin ticket workflow — list, create on behalf of any company, add notes,
// log time, schedule resources.
export const adminTicketsAPI = {
  list: (params) => api.get('/admin/tickets', { params }),
  getStatuses: (ticketId) => api.get(`/admin/tickets/${ticketId}/statuses`),
  updateStatus: (ticketId, statusId) =>
    api.patch(`/admin/tickets/${ticketId}/status`, { statusId }),
  create: (data) => api.post('/admin/tickets', data),
  addNote: (ticketId, data) => api.post(`/admin/tickets/${ticketId}/notes`, data),
  deleteNote: (ticketId, noteId) =>
    api.delete(`/admin/tickets/${ticketId}/notes/${noteId}`),
  notifyPreview: (ticketId) => api.get(`/admin/tickets/${ticketId}/notify-preview`),
  // Portal assignees: staff who work tickets through the portal but hold no
  // ConnectWise licence, so CW cannot put them in the Owner field.
  portalAssigneeCandidates: () => api.get('/admin/tickets/portal-assignees/candidates'),
  getPortalAssignee: (ticketId) => api.get(`/admin/tickets/${ticketId}/portal-assignee`),
  setPortalAssignee: (ticketId, portalUserId) =>
    api.put(`/admin/tickets/${ticketId}/portal-assignee`, { portalUserId }),
  clearPortalAssignee: (ticketId) => api.delete(`/admin/tickets/${ticketId}/portal-assignee`),
  // Co-managed IT: whether the ticket's company is co-managed, and handing it back to its team.
  comanaged: (ticketId) => api.get(`/admin/tickets/${ticketId}/comanaged`),
  returnToClient: (ticketId, note) => api.post(`/admin/tickets/${ticketId}/return-to-client`, { note }),
  listEmailConnectors: () => api.get('/admin/email-connectors'),
  logTime: (ticketId, data) => api.post(`/admin/tickets/${ticketId}/time-entries`, data),
  listSchedule: (ticketId) => api.get(`/admin/tickets/${ticketId}/schedule-entries`),
  addSchedule: (ticketId, data) => api.post(`/admin/tickets/${ticketId}/schedule-entries`, data),
  deleteSchedule: (ticketId, entryId) =>
    api.delete(`/admin/tickets/${ticketId}/schedule-entries/${entryId}`),
  markScheduleDone: (ticketId, entryId, done = true) =>
    api.patch(`/admin/tickets/${ticketId}/schedule-entries/${entryId}/done`, { done }),
  getMemberSchedule: (memberId, from, to) =>
    api.get(`/admin/members/${memberId}/schedule`, { params: { from, to } }),
  // Resource assignments (members assigned without a time block)
  addResource: (ticketId, data) => api.post(`/admin/tickets/${ticketId}/resources`, data),
  deleteResource: (ticketId, entryId) =>
    api.delete(`/admin/tickets/${ticketId}/resources/${entryId}`),
  // Change the primary owner of a ticket
  changeOwner: (ticketId, data) => api.patch(`/admin/tickets/${ticketId}/owner`, data),
  // Edit common ticket fields (summary, board, status, priority, type, etc.)
  updateTicket: (ticketId, data) => api.patch(`/admin/tickets/${ticketId}`, data),
  // Time entries
  listTimeEntries: (ticketId) => api.get(`/admin/tickets/${ticketId}/time-entries`),
  updateTimeEntry: (ticketId, entryId, data) =>
    api.patch(`/admin/tickets/${ticketId}/time-entries/${entryId}`, data),
  deleteTimeEntry: (ticketId, entryId) =>
    api.delete(`/admin/tickets/${ticketId}/time-entries/${entryId}`),
  // Edit existing schedule entry
  updateSchedule: (ticketId, entryId, data) =>
    api.patch(`/admin/tickets/${ticketId}/schedule-entries/${entryId}`, data),
  // Configurations
  listConfigurations: (ticketId) => api.get(`/admin/tickets/${ticketId}/configurations`),
  linkConfiguration: (ticketId, configurationId) =>
    api.post(`/admin/tickets/${ticketId}/configurations`, { configurationId }),
  unlinkConfiguration: (ticketId, configId) =>
    api.delete(`/admin/tickets/${ticketId}/configurations/${configId}`),
};

// Admin lookups for the ticket workflow modals.
export const adminLookupAPI = {
  companies: (q) => api.get('/admin/companies/search-cw', { params: { q } }),
  boards: () => api.get('/admin/lookups/boards'),
  boardStatuses: (boardId) => api.get(`/admin/lookups/boards/${boardId}/statuses`),
  boardTypes: (boardId) => api.get(`/admin/lookups/boards/${boardId}/types`),
  boardSubTypes: (boardId, typeId) =>
    api.get(`/admin/lookups/boards/${boardId}/types/${typeId}/subtypes`),
  boardItems: (boardId) => api.get(`/admin/lookups/boards/${boardId}/items`),
  priorities: () => api.get('/admin/lookups/priorities'),
  sources: () => api.get('/admin/lookups/sources'),
  workTypes: () => api.get('/admin/lookups/work-types'),
  workRoles: () => api.get('/admin/lookups/work-roles'),
  scheduleStatuses: () => api.get('/admin/lookups/schedule-statuses'),
  scheduleWheres: () => api.get('/admin/lookups/schedule-wheres'),
  members: () => api.get('/admin/lookups/members'),
  customFields: (screen) => api.get(`/admin/lookups/custom-fields/${screen}`),
  companyAgreements: (companyId) => api.get(`/admin/companies/${companyId}/agreements`),
  contactSearch: (companyId, q) =>
    api.get(`/admin/companies/${companyId}/contacts/search`, { params: { q } }),
  configurationSearch: (companyId, q) =>
    api.get(`/admin/companies/${companyId}/configurations/search`, { params: { q } }),
};

// Map a portal user to a CW member (optional — enables native CW attribution).
export const adminCwMemberAPI = {
  searchMembers: (q) => api.get('/admin/cw-members/search', { params: { q } }),
  getSuggestion: (userId) => api.get(`/admin/users/${userId}/cw-member-suggestions`),
  setMapping: (userId, cwMemberId) =>
    api.patch(`/admin/users/${userId}/cw-member`, { cwMemberId }),
};

export const adminARAPI = {
  getStats: () => api.get('/admin/ar-stats'),
};

// Admin client payment management (managing payment methods, recurring, autopay for clients)
export const adminClientPaymentsAPI = {
  // Payment Methods
  getPaymentMethods: (companyId) => api.get(`/admin/payments/companies/${companyId}/methods`),
  beginSavedMethod: (companyId, methodType = 'card') => api.post(`/admin/payments/companies/${companyId}/methods/begin`, { methodType }),
  addCard: (companyId, data) => api.post(`/admin/payments/companies/${companyId}/methods/card`, data),
  addBank: (companyId, data) => api.post(`/admin/payments/companies/${companyId}/methods/bank`, data),
  deletePaymentMethod: (companyId, methodId) => api.delete(`/admin/payments/companies/${companyId}/methods/${methodId}`),
  updatePaymentMethod: (companyId, methodId, data) => api.patch(`/admin/payments/companies/${companyId}/methods/${methodId}`, data),
  setDefaultPaymentMethod: (companyId, methodId) => api.patch(`/admin/payments/companies/${companyId}/methods/${methodId}/default`),

  // Recurring Payments
  getRecurringPayments: (companyId) => api.get(`/admin/payments/companies/${companyId}/recurring`),
  createRecurringPayment: (companyId, data) => api.post(`/admin/payments/companies/${companyId}/recurring`, data),
  updateRecurringPayment: (companyId, recurringId, data) => api.patch(`/admin/payments/companies/${companyId}/recurring/${recurringId}`, data),
  cancelRecurringPayment: (companyId, recurringId) => api.delete(`/admin/payments/companies/${companyId}/recurring/${recurringId}`),
  reassignRecurringPayment: (recurringId, newCompanyId) => api.patch(`/admin/payments/recurring/${recurringId}/reassign`, { companyId: newCompanyId }),

  // AutoPay
  getAutoPay: (companyId) => api.get(`/admin/payments/companies/${companyId}/autopay`),
  configureAutoPay: (companyId, data) => api.put(`/admin/payments/companies/${companyId}/autopay`, data),
  // AutoPay charging (Phase 7e): the tenant's switch, the preview and resolving a run
  getAutopayPreview: (companyId) => api.get('/admin/payments/autopay/preview', { params: companyId ? { companyId } : {} }),
  getAutopayChargingSettings: () => api.get('/admin/payments/autopay/settings'),
  saveAutopayChargingSettings: (data) => api.put('/admin/payments/autopay/settings', data),
  resolveAutopayRun: (runId, data) => api.post(`/admin/payments/autopay/runs/${runId}/resolve`, data),

  // Agreement Details (for recurring payment setup)
  getAgreementDetails: (companyId, agreementId) => api.get(`/admin/payments/companies/${companyId}/agreements/${agreementId}`),

  // Credits
  getCredits: (companyId) => api.get(`/admin/payments/companies/${companyId}/credits`),
  createCredit: (companyId, data) => api.post(`/admin/payments/companies/${companyId}/credits`, data),
  applyCredit: (companyId, creditId, data) => api.post(`/admin/payments/companies/${companyId}/credits/${creditId}/apply`, data),

  // Payment History
  getUnifiedHistory: (companyId) => api.get(`/admin/payments/companies/${companyId}/unified-history`),

  // ARB Subscription Import
  getARBSubscriptions: (status = 'active') => api.get('/admin/payments/arb-subscriptions', { params: { status } }),
  importARBSubscriptions: (subscriptions) => api.post('/admin/payments/arb-subscriptions/import', { subscriptions }),
};

export const adminCompanyAPI = {
  // List and search
  list: (params) => api.get('/admin/companies', { params }),
  export: (params) => api.get('/admin/companies/export', { params, responseType: 'blob' }),
  getTypes: () => api.get('/admin/companies/types'),
  syncAll: () => api.post('/admin/companies/sync'),

  // Company data
  getContacts: (companyId) => api.get(`/admin/companies/${companyId}/contacts`),
  getTickets: (companyId, params) => api.get(`/admin/companies/${companyId}/tickets`, { params }),
  getAgreements: (companyId) => api.get(`/admin/companies/${companyId}/agreements`),
  getConfigurations: (companyId, params) => api.get(`/admin/companies/${companyId}/configurations`, { params }),
  getProjects: (companyId) => api.get(`/admin/companies/${companyId}/projects`),
  getOpportunities: (companyId) => api.get(`/admin/companies/${companyId}/opportunities`),
  getPaymentMethods: (companyId) => api.get(`/admin/companies/${companyId}/payment-methods`),
  getAutopay: (companyId) => api.get(`/admin/companies/${companyId}/autopay`),

  // Notes
  getNotes: (companyId) => api.get(`/admin/companies/${companyId}/notes`),
  addNote: (companyId, note) => api.post(`/admin/companies/${companyId}/notes`, { note }),
  updateNote: (companyId, noteId, data) => api.patch(`/admin/companies/${companyId}/notes/${noteId}`, data),
  deleteNote: (companyId, noteId) => api.delete(`/admin/companies/${companyId}/notes/${noteId}`),

  // Tags
  getAllTags: () => api.get('/admin/companies/tags'),
  getTags: (companyId) => api.get(`/admin/companies/${companyId}/tags`),
  assignTag: (companyId, tagId) => api.post(`/admin/companies/${companyId}/tags/${tagId}`),
  removeTag: (companyId, tagId) => api.delete(`/admin/companies/${companyId}/tags/${tagId}`),

  // Settings

  // Payment settings (per-company surcharge overrides)
  getPaymentSettings: (companyId) => api.get(`/admin/companies/${companyId}/payment-settings`),
  updatePaymentSettings: (companyId, settings) => api.put(`/admin/companies/${companyId}/payment-settings`, settings),

};

export const adminFeatureFlagsAPI = {
  list: () => api.get('/admin/feature-flags'),
  update: (key, data) => api.patch(`/admin/feature-flags/${key}`, data),
  getCompanyFlags: (companyId) => api.get(`/admin/companies/${companyId}/feature-flags`),
  setCompanyOverride: (companyId, key, value) =>
    api.put(`/admin/companies/${companyId}/feature-flags/${key}`, { value }),
  removeCompanyOverride: (companyId, key) =>
    api.delete(`/admin/companies/${companyId}/feature-flags/${key}`),
};

export const adminSettingsAPI = {
  get: (key) => api.get(`/admin/settings/${key}`),
  set: (key, value, description) => api.put(`/admin/settings/${key}`, { value, description }),
};

export const portalSettingsAPI = {
  get: () => api.get('/admin/portal-settings'),
  update: (settings) => api.put('/admin/portal-settings', settings),
};

// Admin → Integrations → Ticket Alerts (Slack). Gated by module.ticket_alerts.
// Time: ConnectWise entries, the portal's own ledger, and the work nobody
// logged. Its own permission, because it exposes labour cost across every
// company.
export const adminTimeAPI = {
  // Every filter is optional. Passed straight through as query params so a
  // filtered view is a URL.
  report: (params = {}) => api.get('/admin/time', { params }),
  // Through axios rather than a plain link, so the JWT goes with it.
  exportCsv: (params = {}) => api.get('/admin/time/export', { params, responseType: 'blob' }),
};

export const ticketAlertsAPI = {
  get: () => api.get('/admin/integrations/ticket-alerts'),
  update: (config) => api.put('/admin/integrations/ticket-alerts', config),
  runTimeReminder: () => api.post('/admin/integrations/ticket-alerts/time-reminder/run'),
  testConnection: () => api.post('/admin/integrations/ticket-alerts/cw/test'),
  reregisterCallbacks: () => api.post('/admin/integrations/ticket-alerts/cw/reregister'),
  // Slack
  slackInstallUrl: () => api.get('/admin/integrations/ticket-alerts/slack/install-url'),
  disconnectSlack: () => api.delete('/admin/integrations/ticket-alerts/slack'),
  slackChannels: () => api.get('/admin/integrations/ticket-alerts/slack/channels'),
  // Google Chat (service-account JSON, stored encrypted server-side)
  connectGchat: (serviceAccountJson, projectNumber) =>
    api.post('/admin/integrations/ticket-alerts/gchat/connect', { serviceAccountJson, projectNumber: projectNumber || null }),
  disconnectGchat: () => api.delete('/admin/integrations/ticket-alerts/gchat'),
  connectTeams: ({ appId, appPassword, tenantId, appName }) =>
    api.post('/admin/integrations/ticket-alerts/teams/connect', { appId, appPassword, tenantId: tenantId || null, appName: appName || null }),
  disconnectTeams: () => api.delete('/admin/integrations/ticket-alerts/teams'),
  teamsChannels: () => api.get('/admin/integrations/ticket-alerts/teams/channels'),
  // AI: a base URL, a model and an optional key. No vendor is named, because
  // every candidate endpoint speaks the same chat-completions shape.
  saveAi: (payload) => api.put('/admin/integrations/ticket-alerts/ai', payload),
  testAi: () => api.post('/admin/integrations/ticket-alerts/ai/test'),
  gchatSpaces: () => api.get('/admin/integrations/ticket-alerts/gchat/spaces'),
  inviteGchatTechnicians: () => api.post('/admin/integrations/ticket-alerts/gchat/invite'),
  setGchatProjectNumber: (projectNumber) =>
    api.post('/admin/integrations/ticket-alerts/gchat/project-number', { projectNumber }),
  // Technicians (per platform)
  members: (refresh = false, platform = 'slack') =>
    api.get('/admin/integrations/ticket-alerts/members', { params: { refresh, platform } }),
  setMemberMapping: (externalUserId, data, platform = 'slack') =>
    api.put(`/admin/integrations/ticket-alerts/members/${encodeURIComponent(externalUserId)}`, data, { params: { platform } }),
  // Testing, digest, status
  sendTestAlert: (ticketId, channelId, platform) =>
    api.post('/admin/integrations/ticket-alerts/test-alert', { ticketId, channelId: channelId || null, platform: platform || null }),
  events: (limit = 50) => api.get('/admin/integrations/ticket-alerts/events', { params: { limit } }),
  retryJob: (id) => api.post(`/admin/integrations/ticket-alerts/jobs/${id}/retry`),
  // Count open tickets with no card, then (separately) card them.
  catchUpPreview: (days = 14) => api.get('/admin/integrations/ticket-alerts/catchup', { params: { days } }),
  catchUp: (days = 14) => api.post('/admin/integrations/ticket-alerts/catchup', { days }),
  sendDigestNow: (boardId, routeId = null) =>
    api.post(`/admin/integrations/ticket-alerts/boards/${boardId}/digest${routeId ? `?routeId=${encodeURIComponent(routeId)}` : ''}`),
};

export const adminRolesAPI = {
  list: () => api.get('/admin/roles'),
  create: (data) => api.post('/admin/roles', data),
  delete: (id) => api.delete(`/admin/roles/${id}`),
};

// Customer-screen display customization (label/visibility overrides).
// Effective settings live at three levels — registry defaults, global
// overrides, per-company overrides — merged server-side for the customer read.
export const displaySettingsAPI = {
  getEffective: () => api.get('/display/settings'),
  adminGetGlobal: () => api.get('/admin/portal-settings/display'),
  adminUpdateGlobal: (overrides) => api.put('/admin/portal-settings/display', { overrides }),
  adminGetCompany: (companyId) => api.get(`/admin/companies/${companyId}/display-settings`),
  adminUpdateCompany: (companyId, payload) => api.put(`/admin/companies/${companyId}/display-settings`, payload),
};

// Password Vault API
export const vaultAPI = {
  // Passwords
  listPasswords: (params) => api.get('/vault/passwords', { params }),
  getPassword: (id) => api.get(`/vault/passwords/${id}`),
  revealField: (id, field) => api.post(`/vault/passwords/${id}/reveal`, { field }),
  createPassword: (data) => api.post('/vault/passwords', data),
  updatePassword: (id, data) => api.put(`/vault/passwords/${id}`, data),
  deletePassword: (id) => api.delete(`/vault/passwords/${id}`),
  getPasswordHistory: (id) => api.get(`/vault/passwords/${id}/history`),

  // Bulk operations
  bulkDeletePasswords: (ids) => api.post('/vault/passwords/bulk-delete', { ids }),

  // Access management
  listAccess: (passwordId) => api.get(`/vault/passwords/${passwordId}/access`),
  revokeAccess: (passwordId, accessId) => api.delete(`/vault/passwords/${passwordId}/access/${accessId}`),

  // TOTP
  getTOTP: (passwordId) => api.get(`/vault/passwords/${passwordId}/totp`),
};

// Documentation/Knowledge Base API
export const documentationAPI = {
  // Articles
  listArticles: (params) => api.get('/documentation/articles', { params }),
  getArticle: (id, params) => api.get(`/documentation/articles/${id}`, { params }),
  createArticle: (data) => api.post('/documentation/articles', data),
  updateArticle: (id, data) => api.put(`/documentation/articles/${id}`, data),
  deleteArticle: (id, hard = false) => api.delete(`/documentation/articles/${id}`, { params: { hard } }),
  restoreArticleVersion: (id, versionId) => api.post(`/documentation/articles/${id}/restore-version/${versionId}`),
  publishArticle: (id) => api.post(`/documentation/articles/${id}/publish`),
  unpublishArticle: (id) => api.post(`/documentation/articles/${id}/unpublish`),
  duplicateArticle: (id) => api.post(`/documentation/articles/${id}/duplicate`),

  // Folders
  listFolders: (params) => api.get('/documentation/folders', { params }),
  getFolderTree: (params) => api.get('/documentation/folders/tree', { params }),
  createFolder: (data) => api.post('/documentation/folders', data),
  updateFolder: (id, data) => api.put(`/documentation/folders/${id}`, data),
  deleteFolder: (id) => api.delete(`/documentation/folders/${id}`),
  getPopularTags: (limit) => api.get('/documentation/tags/popular', { params: { limit } }),
  autocompleteTags: (q, limit) => api.get('/documentation/tags/autocomplete', { params: { q, limit } }),
  listConfigurations: (params) => api.get('/documentation/configurations', { params }),
  getConfiguration: (id) => api.get(`/documentation/configurations/${id}`),
  createConfiguration: (data) => api.post('/documentation/configurations', data),
  updateConfiguration: (id, data) => api.put(`/documentation/configurations/${id}`, data),
  deleteConfiguration: (id) => api.delete(`/documentation/configurations/${id}`),
  getConfigurationTypes: () => api.get('/documentation/configurations/types'),
  getLocationsForSelect: (companyId) => api.get('/documentation/locations/select', { params: { companyId } }),

  // Flexible Assets (custom asset types)
  listFlexibleAssets: (params) => api.get('/documentation/flexible-assets', { params }),
  getFlexibleAsset: (id) => api.get(`/documentation/flexible-assets/${id}`),
  createFlexibleAsset: (data) => api.post('/documentation/flexible-assets', data),
  updateFlexibleAsset: (id, data) => api.put(`/documentation/flexible-assets/${id}`, data),
  deleteFlexibleAsset: (id) => api.delete(`/documentation/flexible-assets/${id}`),
  listAssetTypes: (params) => api.get('/documentation/flexible-assets/types', { params }),
  createAssetType: (data) => api.post('/documentation/flexible-assets/types', data),
  updateAssetType: (id, data) => api.put(`/documentation/flexible-assets/types/${id}`, data),
  deleteAssetType: (id) => api.delete(`/documentation/flexible-assets/types/${id}`),
  cloneAssetType: (id) => api.post(`/documentation/flexible-assets/types/${id}/clone`),
  getRelationsFor: (type, id, params) => api.get(`/documentation/relations/for/${type}/${id}`, { params }),
  searchRelatable: (q, excludeType, excludeId, params) => api.get('/documentation/relations/search', { params: { q, excludeType, excludeId, ...params } }),
  createRelation: (data) => api.post('/documentation/relations', data),
  deleteRelation: (id) => api.delete(`/documentation/relations/${id}`),
  listExpirations: (params) => api.get('/documentation/expirations', { params }),
  createExpiration: (data) => api.post('/documentation/expirations', data),
  updateExpiration: (id, data) => api.put(`/documentation/expirations/${id}`, data),
  deleteExpiration: (id) => api.delete(`/documentation/expirations/${id}`),
  renewExpiration: (id, data) => api.post(`/documentation/expirations/${id}/renew`, data),
  getExpirationSummary: (params) => api.get('/documentation/expirations/summary', { params }),

  // Attachments (file uploads)
  listAttachments: (assetType, assetId, params) => api.get(`/documentation/attachments/${assetType}/${assetId}`, { params }),
  uploadAttachment: (assetType, assetId, formData) => api.post(`/documentation/attachments/${assetType}/${assetId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  downloadAttachment: (id) => api.get(`/documentation/attachments/download/${id}`, { responseType: 'blob' }),
  deleteAttachment: (id) => api.delete(`/documentation/attachments/${id}`),

  // Global Search
  globalSearch: (params) => api.get('/documentation/search', { params }),
  getActivity: (params) => api.get('/documentation/activity', { params }),
};

// Admin Alerts API (payment failures, fraud holds, subscription events)
export const adminAlertsAPI = {
  list: (params) => api.get('/admin/alerts', { params }),
  getSummary: () => api.get('/admin/alerts/summary'),
  markAsRead: (id) => api.patch(`/admin/alerts/${id}/read`),
  dismiss: (id) => api.patch(`/admin/alerts/${id}/dismiss`),
  markAllAsRead: () => api.post('/admin/alerts/mark-all-read'),
};

// Deposits API (quote deposits)
export const depositsAPI = {
  // Authenticated user endpoints
  getStatus: (opportunityId) => api.get(`/deposits/${opportunityId}`),
  pay: (opportunityId, data) => api.post(`/deposits/${opportunityId}/pay`, data),
  beginPay: (opportunityId, data) => api.post(`/deposits/${opportunityId}/pay/begin`, data),
  beginPayPublic: (token, data) => api.post(`/deposits/public/${token}/pay/begin`, data),
  markSendingCheckAuth: (opportunityId) => api.post(`/deposits/${opportunityId}/sending-check`),

  // Public endpoints (for shared link payments)
  payPublic: (token, data) => api.post(`/deposits/public/${token}/pay`, data),
  markSendingCheck: (token) => api.post(`/deposits/public/${token}/sending-check`),

  // Admin endpoints
  list: (params) => api.get('/deposits', { params }),
  getInvoiceInfo: (invoiceId) => api.get(`/deposits/invoice-info/${invoiceId}`),
  linkInvoice: (id, payload) => {
    // Back-compat: accept either an invoiceId or { invoiceId, applyAsPayment }
    const body = typeof payload === 'object' && payload !== null
      ? payload
      : { invoiceId: payload };
    return api.post(`/deposits/${id}/link-invoice`, body);
  },
  waive: (id, reason) => api.post(`/deposits/${id}/waive`, { reason }),
};

// Refunds (Phase 7d). `source` is 'payment' (a Pay Now payment) or 'deposit'
// (a quote deposit payment). The preview is what the confirmation step shows;
// the refund body is { amount, reason?, mode: 'gateway'|'external', nonce }.
export const adminRefundsAPI = {
  preview: (source, paymentId) => api.get(`/admin/payments/refunds/${source}/${paymentId}`),
  previewConnectWise: (source, paymentId, amount) =>
    api.get(`/admin/payments/refunds/${source}/${paymentId}/connectwise`, { params: { amount } }),
  // Retry a failed ConnectWise update, or update one not reflected.
  updateConnectWise: (refundId) => api.post(`/admin/payments/refunds/ledger/${refundId}/connectwise`),
};

// Finishing a payment the customer's bank asked them to confirm (Phase 7d).
// Signed in by id; from the emailed link by its signed token.
export const paymentActionsAPI = {
  get: (id) => api.get(`/payment-actions/${id}`),
  complete: (id) => api.post(`/payment-actions/${id}/complete`),
  getByToken: (token) => api.get(`/payment-actions/token/${encodeURIComponent(token)}`),
  completeByToken: (token) => api.post(`/payment-actions/token/${encodeURIComponent(token)}/complete`),
};

/**
 * Contracts: Admin → Contracts. A new draft and a replaced PDF go as
 * multipart (`pdf` + a JSON `data` field); everything else is JSON.
 */
const contractForm = (fields, file) => {
  const form = new FormData();
  form.append('data', JSON.stringify(fields));
  if (file) form.append('pdf', file);
  return form;
};
export const adminContractsAPI = {
  list: (params = {}) => api.get('/admin/contracts', { params }),
  get: (id) => api.get(`/admin/contracts/${id}`),
  create: (fields, file) => api.post('/admin/contracts', contractForm(fields, file), { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, fields, file = null) => api.put(`/admin/contracts/${id}`, contractForm(fields, file), { headers: { 'Content-Type': 'multipart/form-data' } }),
  discard: (id) => api.delete(`/admin/contracts/${id}`),
  markers: (id) => api.get(`/admin/contracts/${id}/markers`),
  original: (id) => api.get(`/admin/contracts/${id}/original`, { params: { inline: 1 }, responseType: 'blob' }),
  signed: (id) => api.get(`/admin/contracts/${id}/signed`, { responseType: 'blob' }),
  send: (id) => api.post(`/admin/contracts/${id}/send`),
  resend: (id, signerId) => api.post(`/admin/contracts/${id}/signers/${signerId}/resend`),
  countersign: (id, body) => api.post(`/admin/contracts/${id}/countersign`, body),
  countersignPackage: (id, body) => api.post(`/admin/contracts/${id}/countersign-package`, body),
  void: (id, reason) => api.post(`/admin/contracts/${id}/void`, { reason }),
  newVersion: (id) => api.post(`/admin/contracts/${id}/new-version`),
  verify: (id) => api.post(`/admin/contracts/${id}/verify`),
  file: (id) => api.post(`/admin/contracts/${id}/file`),
  // The billing agreement built from what was signed.
  settings: () => api.get('/admin/contracts/settings'),
  saveSettings: (settings) => api.put('/admin/contracts/settings', settings),
  catalog: (q) => api.get('/admin/contracts/catalog', { params: { q } }),
  workTypes: () => api.get('/admin/contracts/work-types'),
  rateCardFrom: (agreementId) => api.get(`/admin/contracts/rate-card-from/${agreementId}`),
  agreement: (id) => api.get(`/admin/contracts/${id}/agreement`),
  planAgreement: (id) => api.post(`/admin/contracts/${id}/agreement/plan`),
  updateAgreement: (id, changes) => api.put(`/admin/contracts/${id}/agreement`, changes),
  loadAgreementLines: (id, opportunityId) => api.post(`/admin/contracts/${id}/agreement/load`, { opportunityId }),
  createAgreement: (id) => api.post(`/admin/contracts/${id}/agreement/create`),
  adoptUnderMsa: (id, agreementIds) => api.post(`/admin/contracts/${id}/agreement/adopt`, { agreementIds }),
};

/** The customer's own contracts. */
export const contractsAPI = {
  list: (params = {}) => api.get('/contracts', { params }),
  signed: (id) => api.get(`/contracts/${id}/signed`, { responseType: 'blob' }),
  signLink: (id) => api.post(`/contracts/${id}/sign-link`),
};

/** Signing by the emailed link (public; a signed-in user's token rides along). */
export const signAPI = {
  get: (token) => api.get(`/sign/${encodeURIComponent(token)}`),
  document: (token, download = false) =>
    api.get(`/sign/${encodeURIComponent(token)}/document`, { params: download ? { download: 1 } : {}, responseType: 'blob' }),
  sendCode: (token) => api.post(`/sign/${encodeURIComponent(token)}/code`),
  verify: (token, code) => api.post(`/sign/${encodeURIComponent(token)}/verify`, { code }),
  consent: (token) => api.post(`/sign/${encodeURIComponent(token)}/consent`, { agree: true }),
  sign: (token, body) => api.post(`/sign/${encodeURIComponent(token)}/sign`, body),
  decline: (token, reason) => api.post(`/sign/${encodeURIComponent(token)}/decline`, { reason }),
  next: (token) => api.post(`/sign/${encodeURIComponent(token)}/next`),
};

// Admin → Integrations → providers. Per-tenant credentials for ConnectWise,
// payments, mail, chat and sign-in. Secrets are tri-state on save: leave a
// field out to keep what is stored, send '' to clear it, a value to set it.
// Nothing here ever returns a secret.
export const adminProvidersAPI = {
  list: () => api.get('/admin/integrations/providers'),
  save: (provider, values) => api.put(`/admin/integrations/providers/${provider}`, values),
  verify: (provider, values = {}) => api.post(`/admin/integrations/providers/${provider}/verify`, values),
  disable: (provider) => api.delete(`/admin/integrations/providers/${provider}`),
  // SMTP2GO mode only: register the from address's domain and re-check its DNS
  verifySenderDomain: () => api.post('/admin/integrations/providers/smtp/dns/verify'),
  // The default payment gateway: { gateway, available, labels }
  getPaymentGateway: () => api.get('/admin/integrations/payments/gateway'),
  setPaymentGateway: (gateway) => api.put('/admin/integrations/payments/gateway', { gateway }),
  // Platform payments (Stripe Connect): start or continue onboarding (returns
  // { url } to send the admin to), re-read the account, open its dashboard.
  platformPaymentsOnboard: () => api.post('/admin/integrations/providers/stripe_connect/onboard'),
  platformPaymentsRefresh: () => api.post('/admin/integrations/providers/stripe_connect/refresh'),
  platformPaymentsDashboard: () => api.get('/admin/integrations/providers/stripe_connect/dashboard'),
};

// Client-level branding, admin side (Phase 5d). Overrides are nullable; null
// inherits the MSP's Portal Settings branding.
export const adminBrandingAPI = {
  getCompany: (companyId) => api.get(`/admin/branding/companies/${companyId}`),
  updateCompany: (companyId, overrides) => api.put(`/admin/branding/companies/${companyId}`, { overrides }),
  resetCompany: (companyId) => api.delete(`/admin/branding/companies/${companyId}`),
};

// Co-managed IT, per company: the intake board, the statuses the client's
// technicians may set, where escalation sends a ticket.
export const adminComanagedAPI = {
  getCompany: (companyId) => api.get(`/admin/comanaged/companies/${companyId}`),
  updateCompany: (companyId, settings) => api.put(`/admin/comanaged/companies/${companyId}`, { settings }),
  resetCompany: (companyId) => api.delete(`/admin/comanaged/companies/${companyId}`),
};

// The effective branding for the logged-in user's own company.
export const brandingAPI = {
  // `fresh` gets past the answer's max-age=60 after the brand was saved.
  me: ({ fresh } = {}) => api.get('/branding/me', fresh ? { params: { fresh: Date.now() } } : undefined),
};

// Admin → Domains (Phase 5a + 5g). The tenant's own hostnames on the
// platform. Nothing is served on a hostname until verify reports it so;
// the page polls verify until it does. Branding is per hostname: null or
// blank fields inherit Portal Settings, a null body clears every override.
export const adminDomainsAPI = {
  list: () => api.get('/admin/domains'),
  add: (hostname) => api.post('/admin/domains', { hostname }),
  verify: (id) => api.post(`/admin/domains/${id}/verify`),
  setPrimary: (id) => api.post(`/admin/domains/${id}/primary`),
  saveBranding: (id, branding) => api.put(`/admin/domains/${id}/branding`, { branding }),
  remove: (id) => api.delete(`/admin/domains/${id}`),
};
